// Realtime saga: owns the single WebSocket connection and bridges it to redux.
//
// Connection lifecycle
//   A CONNECT (or the legacy notifications SSE_CONNECT, kept so the bell's
//   existing trigger Just Works) opens the socket and subscribes the base
//   channels (the user channel + the active workspace channel). The loop then
//   blocks until DISCONNECT (logout), so duplicate CONNECTs while already
//   connected are no-ops — both this loop and wsClient.connect() are idempotent.
//   Network drops are handled INSIDE wsClient (backoff reconnect + automatic
//   resubscribe), so they never reach this loop and can't double-count
//   subscriptions.
//
// Event mapping
//   Notifications are delivered on the user channel and mapped to the existing
//   notifications actions (so the bell UI is unchanged after the SSE->WS
//   migration). Every other event is re-dispatched as a generic REALTIME_EVENT
//   that feature reducers/sagas can opt into — see core/WEBSOCKETS.md for the
//   channel + event catalog and the extension pattern.

import { call, put, take, fork, cancel, takeEvery, takeLatest } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';

import realtimeClient from '../../../realtime/wsClient';
import {
  REALTIME_CONNECT, REALTIME_DISCONNECT,
  REALTIME_SUBSCRIBE, REALTIME_UNSUBSCRIBE,
  REALTIME_SUBSCRIBE_WORKSPACE, REALTIME_UNSUBSCRIBE_WORKSPACE,
  realtimeStatus, realtimeEvent,
} from '../../actions/realtime';
import {
  SSE_CONNECT, SSE_DISCONNECT,
  sseStatus, notificationReceived,
} from '../../actions/notifications';
import { fetchRequest as fetchDiscoveryRun } from '../../actions/discovery/run';
import { fetchRequest as fetchGoalDetail } from '../../actions/discovery/goalDetail';

// -------------------------------------------------------------------------
// Channel helpers
// -------------------------------------------------------------------------

// The uid is authoritative in the JWT (the FE doesn't persist USER_ID), so
// decode the `uid` claim from the stored token (unverified — routing hint only,
// the gateway re-validates the JWT on connect).
function decodeJwtUid(token) {
  try {
    const t = (token || '').replace(/^Bearer\s+/i, '');
    const payload = JSON.parse(atob(t.split('.')[1]));
    return payload.uid || null;
  } catch (e) { return null; }
}

function currentUserChannel() {
  try {
    const uid = decodeJwtUid(localStorage.getItem('authToken'));
    return uid ? `user:${uid}` : null;
  } catch (e) { return null; }
}

function currentWorkspaceChannel() {
  try {
    const wid = localStorage.getItem('WORKSPACE_ID');
    return wid ? `workspace:${wid}` : null;
  } catch (e) { return null; }
}

// The workspace channel currently subscribed by the "base" subscription, so
// a workspace switch can release the old one before taking the new.
let activeWorkspaceChannel = null;

// -------------------------------------------------------------------------
// Transport -> saga bridge
// -------------------------------------------------------------------------

function createClientChannel() {
  return eventChannel((emit) => {
    const offEvent = realtimeClient.on('event', (msg) => emit({ kind: 'event', msg }));
    const offStatus = realtimeClient.on('status', (status) => emit({ kind: 'status', status }));
    const offWelcome = realtimeClient.on('welcome', (msg) => emit({ kind: 'welcome', msg }));
    return () => { offEvent(); offStatus(); offWelcome(); };
  });
}

function* handleEvent(msg) {
  // error frames (forbidden subscribe, etc.) — surface generically.
  if (msg.type === 'error') {
    yield put(realtimeEvent(msg));
    return;
  }
  if (msg.type !== 'event') return;

  // Notifications: deliver to the bell. The header pill toast (see
  // NotificationsBell) surfaces every arrival inline for a few seconds,
  // severity-tinted — so no extra mid-screen snackbar, which would
  // double-display the same event.
  if (msg.event === 'notification') {
    yield put(notificationReceived(msg.data || {}));
    return;
  }

  // Goal run lifecycle: refresh the goal's run/detail so the funnel updates
  // live. Gated on the GOAL channel (not the workspace fan-out) so we only
  // refetch while the goal page is open — GoalDetailPage subscribes to
  // `goal:<id>` on mount and drops it on unmount.
  const channel = msg.channel || '';
  if (channel.indexOf('goal:') === 0 && typeof msg.event === 'string' && msg.event.indexOf('goal.run.') === 0) {
    const data = msg.data || {};
    if (data.run_id) {
      yield put(fetchDiscoveryRun({ runId: data.run_id }));
    }
    if ((msg.event === 'goal.run.completed' || msg.event === 'goal.run.failed') && data.goal_id) {
      yield put(fetchGoalDetail({ goalId: data.goal_id }));
    }
  }

  // Everything else (and the above): generic dispatch for feature opt-in.
  yield put(realtimeEvent(msg));
}

function* watchClient(channel) {
  while (true) {
    const ev = yield take(channel);
    if (ev.kind === 'status') {
      yield put(realtimeStatus(ev.status));
      // Bridge to the bell's existing status indicator.
      const mapped =
        ev.status === 'open' ? 'open'
          : ev.status === 'unavailable' || ev.status === 'unauthorized' ? 'unavailable'
            : 'connecting';
      yield put(sseStatus(mapped));
    } else if (ev.kind === 'event') {
      yield* handleEvent(ev.msg);
    }
    // 'welcome' needs no action here — base subscriptions are issued at
    // connect time and wsClient replays them on every (re)connect.
  }
}

// -------------------------------------------------------------------------
// Lifecycle loop
// -------------------------------------------------------------------------

function subscribeBaseChannels() {
  const uc = currentUserChannel();
  if (uc) realtimeClient.subscribe(uc);
  const wc = currentWorkspaceChannel();
  if (wc) {
    activeWorkspaceChannel = wc;
    realtimeClient.subscribe(wc);
  }
}

function* connectionLoop() {
  while (true) {
    yield take([REALTIME_CONNECT, SSE_CONNECT]);
    const channel = yield call(createClientChannel);
    const task = yield fork(watchClient, channel);

    realtimeClient.connect();
    subscribeBaseChannels();

    yield take([REALTIME_DISCONNECT, SSE_DISCONNECT]);

    realtimeClient.disconnect();
    activeWorkspaceChannel = null;
    yield cancel(task);
    channel.close();
  }
}

// -------------------------------------------------------------------------
// Imperative subscribe/unsubscribe watchers
// -------------------------------------------------------------------------

function subscribeHandler({ payload }) {
  if (payload && payload.channel) realtimeClient.subscribe(payload.channel);
}

function unsubscribeHandler({ payload }) {
  if (payload && payload.channel) realtimeClient.unsubscribe(payload.channel);
}

// Swap the active workspace channel (release the previous, take the new).
function switchWorkspaceHandler({ payload }) {
  const wid = payload && payload.workspaceId;
  const next = wid ? `workspace:${wid}` : null;
  if (activeWorkspaceChannel === next) return;
  if (activeWorkspaceChannel) realtimeClient.unsubscribe(activeWorkspaceChannel);
  activeWorkspaceChannel = next;
  if (next) realtimeClient.subscribe(next);
}

function unsubscribeWorkspaceHandler({ payload }) {
  const wid = payload && payload.workspaceId;
  const ch = wid ? `workspace:${wid}` : activeWorkspaceChannel;
  if (ch) realtimeClient.unsubscribe(ch);
  if (ch === activeWorkspaceChannel) activeWorkspaceChannel = null;
}

// -------------------------------------------------------------------------
// Watcher
// -------------------------------------------------------------------------

export function* watch() {
  yield fork(connectionLoop);
  yield takeEvery(REALTIME_SUBSCRIBE, subscribeHandler);
  yield takeEvery(REALTIME_UNSUBSCRIBE, unsubscribeHandler);
  yield takeLatest(REALTIME_SUBSCRIBE_WORKSPACE, switchWorkspaceHandler);
  yield takeEvery(REALTIME_UNSUBSCRIBE_WORKSPACE, unsubscribeWorkspaceHandler);
}
